import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ExportTemplate,
  ExportDealSelectionDocument,
  GetDealSelectionExportDownloadUrlDocument,
  GetDealSelectionExportsDocument,
} from '@/lib/graphql/generated/graphql';
import { trackEvent } from '@/lib/mixpanel';
import {
  type DealSelectionExportFromQuery,
  ExportStatus,
  isExportSettled,
} from '@/types/deal-selection-export';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';

import { mapI18nLanguageToLanguageIso6391 } from '../utils';

const EXPORT_POLL_INTERVAL_MS = 2 * 1000;
const EXPORT_DEADLINE_MS = 60 * 1000;

export const getExportErrorCode = (
  exportItem: DealSelectionExportFromQuery
): string =>
  exportItem.errors[0]?.code ?? exportItem.errors[0]?.step ?? 'export_error';

export function useDealSelectionExport(
  dealId: string,
  template: ExportTemplate
) {
  const [exportsList, setExportsList] = useState<
    DealSelectionExportFromQuery[]
  >([]);
  const [isExporting, setIsExporting] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [pendingExportId, setPendingExportId] = useState<string | null>(null);
  const { i18n, t } = useTranslation();

  const trackedExportIdRef = useRef<string | null>(null);
  const trackedDownloadUrlRef = useRef<string | null>(null);
  const exportSettledRef = useRef(false);

  const [exportDealSelection] = useMutation(ExportDealSelectionDocument, {
    variables: {
      dealId,
      template,
      language: mapI18nLanguageToLanguageIso6391(i18n.language),
    },
    onCompleted(data) {
      const result = data.exportDealSelection;
      if (result.__typename === 'DomainException') {
        setError(new Error(result.code));
        setIsExporting(false);
        setPendingExportId(null);
        trackEvent('EXPORT_FAILED', {
          dealId,
          template,
          errorCode: result.code,
        });
      } else {
        setError(null);
        setIsExporting(true);
        setPendingExportId(result.id);
      }
    },
    onError(err) {
      setError(err);
      setIsExporting(false);
      setPendingExportId(null);
      trackEvent('EXPORT_FAILED', {
        dealId,
        template,
        errorCode: err.message || 'network_error',
      });
    },
  });

  const [getDownloadUrl, { data: urlData, error: urlError }] = useLazyQuery(
    GetDealSelectionExportDownloadUrlDocument,
    {
      fetchPolicy: 'no-cache',
    }
  );

  const { data: exportsData, stopPolling } = useQuery(
    GetDealSelectionExportsDocument,
    {
      variables: { dealId },
      skip: !dealId || !pendingExportId,
      fetchPolicy: 'no-cache',
      pollInterval: EXPORT_POLL_INTERVAL_MS,
    }
  );

  useEffect(() => {
    if (urlError) {
      if (exportSettledRef.current) return;
      exportSettledRef.current = true;
      setError(urlError);
      setIsExporting(false);
      setPendingExportId(null);
      trackEvent('EXPORT_FAILED', {
        dealId,
        template,
        errorCode: urlError.message || 'download_url_error',
      });
    }
  }, [urlError, dealId, template]);

  useEffect(() => {
    if (
      urlData?.dealSelectionExportDownloadUrl &&
      urlData.dealSelectionExportDownloadUrl !== trackedDownloadUrlRef.current
    ) {
      if (exportSettledRef.current) return;
      exportSettledRef.current = true;
      trackedDownloadUrlRef.current = urlData.dealSelectionExportDownloadUrl;
      setDownloadUrl(urlData.dealSelectionExportDownloadUrl);
      setIsExporting(false);
      setPendingExportId(null);
      trackEvent('EXPORT_DOWNLOADED', { dealId, template });
    }
  }, [urlData, dealId, template]);

  useEffect(() => {
    const exports = exportsData?.deal?.selectionExports;
    if (!exports) return;

    setExportsList(exports);

    if (!pendingExportId || exportSettledRef.current) return;

    const trackedExport = exports.find(({ id }) => id === pendingExportId);
    if (!trackedExport || !isExportSettled(trackedExport.status)) return;

    stopPolling();

    if (trackedExport.status === ExportStatus.Error) {
      exportSettledRef.current = true;
      setError(new Error(getExportErrorCode(trackedExport)));
      setIsExporting(false);
      setPendingExportId(null);
      trackEvent('EXPORT_FAILED', {
        dealId,
        template,
        errorCode: getExportErrorCode(trackedExport),
      });
      return;
    }

    if (trackedExportIdRef.current !== trackedExport.id) {
      trackedExportIdRef.current = trackedExport.id;
      trackEvent('EXPORT_COMPLETED', {
        dealId,
        template,
        exportId: trackedExport.id,
      });
    }

    getDownloadUrl({ variables: { exportId: trackedExport.id } });
  }, [
    exportsData,
    pendingExportId,
    getDownloadUrl,
    stopPolling,
    dealId,
    template,
  ]);

  useEffect(() => {
    if (!isExporting || !pendingExportId) return;
    const exportId = pendingExportId;
    let active = true;

    const timeoutId = window.setTimeout(() => {
      void (async () => {
        let isReady = false;
        try {
          const result = await getDownloadUrl({ variables: { exportId } });
          isReady = Boolean(result.data?.dealSelectionExportDownloadUrl);
        } catch {
          isReady = false;
        }
        if (!active) return;
        if (isReady) return;
        if (exportSettledRef.current) return;
        exportSettledRef.current = true;
        setError(new Error(t('general.error_export_timeout')));
        setIsExporting(false);
        setPendingExportId(null);
        trackEvent('EXPORT_FAILED', {
          dealId,
          template,
          errorCode: 'export_timeout',
        });
      })();
    }, EXPORT_DEADLINE_MS);

    return () => {
      active = false;
      window.clearTimeout(timeoutId);
    };
  }, [isExporting, pendingExportId, getDownloadUrl, dealId, template, t]);

  const startExport = useCallback(() => {
    setError(null);
    setDownloadUrl(null);
    setPendingExportId(null);
    exportSettledRef.current = false;
    trackedExportIdRef.current = null;
    trackedDownloadUrlRef.current = null;
    trackEvent('EXPORT_REQUESTED', {
      dealId,
      template,
      language: mapI18nLanguageToLanguageIso6391(i18n.language),
    });
    exportDealSelection();
  }, [exportDealSelection, dealId, template, i18n.language]);

  return {
    exportsList,
    isExporting,
    downloadUrl,
    error,
    startExport,
  };
}
